<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { useRouter, useRoute } from 'vue-router'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import { toastModule } from '@/store/toast/module'

const router = useRouter()
const route = useRoute()

const {
    Button: PocketButton,
    Icon,
    Input: PocketInput,
} = UIKit

const selectedOption = ref<string | null>(null)
const otherText = ref('')
const quizId = ref<string | null>(null)

const options = [
    'Affiliate or influencer',
    'App store',
    'Facebook or Instagram',
    'Friend, classmate, or coworker',
    'Google Search',
    'Reddit',
    'School or instructor referral',
    'Other',
]

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

onMounted(() => {
    if (typeof route.params.quizId === 'string') {
        quizId.value = route.params.quizId
    }
})

const selectOption = (option: string, event?: Event) => {
    // Blur any previously focused option to remove focus border
    const previouslyFocused = document.activeElement as HTMLElement
    if (previouslyFocused?.classList.contains('heard-about-us-ask__option')) {
        previouslyFocused.blur()
    }

    selectedOption.value = selectedOption.value === option ? null : option
    if (option !== 'Other') {
        otherText.value = ''
    }

    // Blur the newly clicked element to ensure only selected border shows, not focus border
    if (event?.currentTarget) {
        (event.currentTarget as HTMLElement).blur()
    }
}

const updateUserWebConfig = async () => {
    await userModule.actions.updateWebConfig({
        hasSeenHeardAboutUsAsk: true,
    })
}

const submit = async () => {
    if (selectedOption.value) {
        const submittedValue = selectedOption.value === 'Other' && otherText.value
            ? otherText.value
            : selectedOption.value

        analyticsModule.actions.amplitudeTrack('How You Heard About Us Submitted', {
            userReportedTrafficSource: submittedValue,
        })

        toastModule.actions.displayToast({
            title: 'Thank you for sharing!',
        })
    }

    await updateUserWebConfig()

    await router.push({
        name: 'study',
    })
}

const goBack = async () => {
    await router.push({
        name: 'quiz-result',
        params: {
            quizId: quizId.value,
        },
    })
}


const skip = async () => {
    await updateUserWebConfig()

    await router.push({
        name: 'study',
    })
}
</script>

<template>
    <div v-dark class="heard-about-us-ask">
        <div v-dark class="heard-about-us-ask__header">
            <PocketButton
                class="heard-about-us-ask__back-button"
                type="secondary"
                tabindex="0"
                role="button"
                aria-label="Back"
                :is-dark-mode="isDarkMode"
                @click="goBack"
                @keydown.enter="goBack"
                @mousedown.prevent
            >
                <Icon
                    class="heard-about-us-ask__back-button-icon"
                    type="paginationArrow"
                    :is-dark-mode="isDarkMode"
                />Back
            </PocketButton>
            <PocketButton
                class="heard-about-us-ask__skip"
                type="secondary"
                tabindex="0"
                role="button"
                aria-label="Skip"
                :is-dark-mode="isDarkMode"
                @click="skip"
                @keydown.enter="skip"
                @mousedown.prevent
            >
                Skip
            </PocketButton>
        </div>
        <div class="heard-about-us-ask__content">
            <div v-dark class="heard-about-us-ask__label">
                Quick Question
            </div>
            <div v-dark class="heard-about-us-ask__question">
                How did you first hear about Pocket Prep?
            </div>
            <div class="heard-about-us-ask__options">
                <template
                    v-for="option in options"
                    :key="option"
                >
                    <div
                        v-dark
                        class="heard-about-us-ask__option"
                        :class="{
                            'heard-about-us-ask__option--selected': selectedOption === option
                        }"
                        tabindex="0"
                        role="radio"
                        :aria-checked="selectedOption === option"
                        :aria-label="option"
                        @click="selectOption(option, $event)"
                        @keydown.enter="selectOption(option, $event)"
                        @mousedown.prevent
                    >
                        {{ option }}
                    </div>
                    <PocketInput
                        v-if="option === 'Other' && selectedOption === 'Other'"
                        v-model="otherText"
                        class="heard-about-us-ask__textarea"
                        :is-dark-mode="isDarkMode"
                    />
                </template>
            </div>
            <PocketButton
                class="heard-about-us-ask__submit"
                :disabled="!selectedOption"
                :is-dark-mode="isDarkMode"
                @click="submit"
            >
                Submit
            </PocketButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.heard-about-us-ask {
    height: 100vh;
    background: $gray-background;
    overflow-y: auto;

    &--dark {
        background: $charcoal;
    }

    @include breakpoint(black-bear) {
        height: calc(100vh + 10px);
    }

    &__header {
        width: 100%;
        height: 64px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 30px 12px 22px;
        box-sizing: border-box;
        background: $white;

        &--dark {
            background: $brand-black
        }

        @include breakpoint(black-bear) {
            padding: 12px 8px 12px 0;

            &--dark {
                background: $charcoal;
            }
        }
    }

    &__back-button {
        :deep(.uikit-btn__content) {
            display: flex;
            align-items: center;
        }

        :deep(svg) {
            width: 24px;
            height: 24px;
            transform: rotate(180deg);
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 55px;

        @include breakpoint(black-bear) {
            margin-top: 35px
        }
    }

    &__label {
        color: $ash;
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        text-align: center;
        margin-bottom: 8px;

        &--dark {
            color: $fog
        }
    }

    &__question {
        color: $brand-black;
        font-size: 26px;
        font-weight: 700;
        text-align: center;
        margin-top: 8px;
        margin-bottom: 44px;
        width: 350px;

        @include breakpoint(black-bear) {
            font-size: 22px;
            line-height: 30px;
            margin-bottom: 32px;
        }

        &--dark {
            color: $white
        }
    }

    &__options {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__option {
        width: 492px;
        display: flex;
        align-items: center;
        padding: 12px 15px;
        background: $white;
        position: relative;
        outline: none;
        cursor: pointer;
        font-size: 16px;
        font-weight: 500;
        line-height: 23px;
        letter-spacing: -0.1px;

        &::after {
            content: '';
            position: absolute;
            top: -1px;
            bottom: -1px;
            left: -1px;
            right: -1px;
            border: 0.5px solid rgba($pewter, 0.85);
            box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
            border-radius: 6px;
            pointer-events: none;
        }

        @media (hover: hover) {
            &:hover::after {
                display: block;
                top: -2px;
                bottom: -2px;
                left: -2px;
                right: -2px;
                border: 1.5px solid $slate;
                border-radius: 7px;
            }
        }

        &:focus::after {
            display: block;
            border: 1px solid $brand-blue;
        }

        &--selected {
            &::after {
                display: block;
                border: 2px solid $brand-blue;
            }

            @media (hover: hover) {
                &:hover::after {
                    border: 2px solid $brand-blue;
                }
            }

            &--dark {
                &::after {
                    border-color: $banana-bread !important;
                }

                @media (hover: hover) {
                    &:hover::after {
                        border-color: $banana-bread !important;
                    }
                }
            }
        }

        &--dark {
            background: $brand-black;
            color: $barely-background;

            &::after {
                border-color: $slate;
                box-shadow: 0 1px 4px 0 rgba($jet, 0.3);
            }

            &:focus::after {
                display: block;
                border-color: $banana-bread !important;
            }

        }

        @include breakpoint(black-bear) {
            width: 315px;
        }
    }

    &__textarea {
        :deep(.uikit-textarea__textarea) {
            resize: none;
        }
    }

    &__submit {
        margin-top: 32px;
        margin-bottom: 32px;
    }
}
</style>
